import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import { Card } from '@/components/ui/card';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select';
import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { useEntityForm } from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useTicketGenerator } from '@/hooks/useTicketGenerator';
import { VehicleAutocomplete } from '@/components/ui/vehicle-autocomplete';
import {
	ContractAutocomplete,
	type ContractAutocompleteItem
} from '@/components/ui/contract-autocomplete';
import { CONTRACT_EFFECTIVE_VEHICLES_QUERY } from '@/pages/contracts/contracts.gql';
import { ParkingSessionStatus } from '@/graphql/generated';
import type { VehicleViewModel } from '@/graphql/generated';

import type { ParkingSessionFormSubmitData } from './parking-session-input';

const createParkingSessionFormSchema = (t: (key: string) => string, mode: 'create' | 'edit') =>
	z.object({
		plateNumber: z
			.string()
			.max(50, t('parkingSessions:validation.plateNumber.maxLength'))
			.optional()
			.or(z.literal(''))
			.transform(val => val?.trim() ?? ''),
		ticketId: z
			.string()
			.max(50, t('parkingSessions:validation.ticketId.maxLength'))
			.optional()
			.nullable()
			.or(z.literal('')),
		contractId: z.string().optional().nullable(),
		status: mode === 'edit'
			? z.nativeEnum(ParkingSessionStatus)
			: z.nativeEnum(ParkingSessionStatus).optional()
	});

export type ParkingSessionFormValues = z.infer<
	ReturnType<typeof createParkingSessionFormSchema>
>;

interface ParkingSessionFormProps {
	mode: 'create' | 'edit';
	initialData?: ParkingSessionFormSubmitData;
	contractInitialLabel?: string;
	onSubmit: (data: ParkingSessionFormSubmitData) => void;
	isSubmitting: boolean;
	headerTitle: string;
	onBack?: () => void;
}

export const ParkingSessionForm = ({
	mode,
	initialData,
	contractInitialLabel,
	onSubmit,
	isSubmitting,
	headerTitle,
	onBack
}: ParkingSessionFormProps) => {
	const { t } = useTranslation();
	const parkingSessionFormSchema = useMemo(
		() => createParkingSessionFormSchema(t, mode),
		[t, mode]
	);

	const form = useEntityForm<ParkingSessionFormValues>({
		mode,
		schema: parkingSessionFormSchema,
		defaultValues: {
			plateNumber: '',
			ticketId: '',
			contractId: null,
			status: undefined
		},
		initialData: initialData as ParkingSessionFormValues | undefined
	});

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	const { control, handleSubmit, setValue, watch, setError, clearErrors } = form;

	// Generate ticket ID automatically for create mode
	const { generateDynamic, isReady } = useTicketGenerator(0); // siteId 0

	useEffect(() => {
		if (mode === 'create' && isReady) {
			generateDynamic().then(ticketId => {
				setValue('ticketId', ticketId);
			});
		}
	}, [mode, isReady, generateDynamic, setValue]);

	// Track selected vehicle for display in autocomplete
	const plateNumber = watch('plateNumber');
	const [selectedVehicleId, setSelectedVehicleId] = useState<string>(
		initialData?.vehicleId ?? ''
	);

	// Track selected contract and its effective vehicles for validation
	const [selectedContractId, setSelectedContractId] = useState<string>(
		initialData?.contractId ?? ''
	);
	const [effectiveVehicleIds, setEffectiveVehicleIds] = useState<Set<string>>(new Set());

	// Lazily fetch effective vehicles only when a contract is selected
	const [fetchEffectiveVehicles] = useLazyQuery(CONTRACT_EFFECTIVE_VEHICLES_QUERY, {
		fetchPolicy: 'network-only',
	});

	// Fetch effective vehicles on initial load when editing with an existing contract
	useEffect(() => {
		const contractId = initialData?.contractId;
		if (contractId) {
			fetchEffectiveVehicles({ variables: { contractId } }).then(({ data }) => {
				const ids = new Set<string>(
					data?.contract?.effectiveVehicles?.map((v: { vehicleId: string }) => v.vehicleId) ?? []
				);
				setEffectiveVehicleIds(ids);
			}).catch((error) => {
				console.error('Failed to fetch initial effective vehicles:', error);
			});
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const checkVehicleAgainstContract = useCallback((vehicleId: string, vehicleIds: Set<string>, contractId: string): boolean => {
		if (!contractId || !vehicleId) {
			clearErrors('plateNumber');
			return true;
		}
		// Contract selected: vehicle must be in the effective vehicles list
		// (empty list means NO vehicles are allowed for this contract)
		if (!vehicleIds.has(vehicleId)) {
			setError('plateNumber', {
				type: 'manual',
				message: t('parkingSessions:validation.contract.vehicleNotAllowed'),
			});
			return false;
		}
		clearErrors('plateNumber');
		return true;
	}, [t, setError, clearErrors]);

	const handleVehicleChange = (
		vehicleId: string,
		vehicle: VehicleViewModel | null
	) => {
		setSelectedVehicleId(vehicleId);
		if (vehicle?.plateNumber) {
			setValue('plateNumber', vehicle.plateNumber, { shouldValidate: true, shouldDirty: true });
		} else {
			setValue('plateNumber', '', { shouldValidate: true, shouldDirty: true });
		}
		checkVehicleAgainstContract(vehicleId, effectiveVehicleIds, selectedContractId);
	};

	const handleContractChange = async (
		contractId: string,
		_contract: ContractAutocompleteItem | null
	) => {
		setSelectedContractId(contractId);
		setValue('contractId', contractId || null, { shouldDirty: true });

		if (!contractId) {
			setEffectiveVehicleIds(new Set());
			clearErrors('plateNumber');
			return;
		}

		// Fetch effective vehicles lazily only for the selected contract
		try {
			const { data } = await fetchEffectiveVehicles({ variables: { contractId } });
			const newEffectiveIds = new Set<string>(
				data?.contract?.effectiveVehicles?.map((v: { vehicleId: string }) => v.vehicleId) ?? []
			);
			setEffectiveVehicleIds(newEffectiveIds);
			checkVehicleAgainstContract(selectedVehicleId, newEffectiveIds, contractId);
		} catch (error) {
			console.error('Failed to fetch effective vehicles for contract:', error);
			setEffectiveVehicleIds(new Set());
		}
	};

	const handleFormSubmit = (values: ParkingSessionFormValues) => {
		// Check contract-vehicle validation before submitting
		if (!checkVehicleAgainstContract(selectedVehicleId, effectiveVehicleIds, selectedContractId)) {
			return;
		}

		onSubmit({
			...values,
			parkingSessionId: initialData?.parkingSessionId,
			vehicleId: selectedVehicleId || undefined,
			contractId: selectedContractId || null,
			status: values.status as ParkingSessionStatus | undefined
		});
	};

	const statusOptions = [
		{ value: ParkingSessionStatus.Inside, labelKey: 'parkingSessions:list.filters.inside' },
		{ value: ParkingSessionStatus.Exited, labelKey: 'parkingSessions:list.filters.exited' },
		{ value: ParkingSessionStatus.Canceled, labelKey: 'parkingSessions:list.filters.canceled' }
	];

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				rightContent={
					<FormActionButtons
						mode={mode}
						isSubmitting={isSubmitting}
						validationErrors={form.formState.errors}
						formId="parking-session-form"
					/>
				}
			/>

			<Form {...form}>
				<form
					id="parking-session-form"
					onSubmit={handleSubmit(handleFormSubmit)}
					className="space-y-6"
				>
					<Card className="p-6">
						<div className="grid gap-6 md:grid-cols-2">
							{/* Vehicle / Plate Number Field */}
							<FormField
								control={control}
								name="plateNumber"
								render={() => (
									<FormItem>
										<FormLabel>
											{t('parkingSessions:form.vehicle')}
										</FormLabel>
										<VehicleAutocomplete
											value={selectedVehicleId}
											onChange={handleVehicleChange}
											placeholder={t(
												'parkingSessions:form.vehiclePlaceholder'
											)}
											enableCreateNew={true}
											className="w-full"
											openDelay={500}
											initialSelectedLabel={plateNumber || undefined}
											autoFocus={mode === 'create'}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Contract Field */}
							<FormField
								control={control}
								name="contractId"
								render={() => (
									<FormItem>
										<FormLabel>
											{t('parkingSessions:form.contract')}
										</FormLabel>
										<ContractAutocomplete
											value={selectedContractId}
											onChange={handleContractChange}
											placeholder={t(
												'parkingSessions:form.contractPlaceholder'
											)}
											className="w-full"
											initialSelectedLabel={contractInitialLabel}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Ticket ID Field (read-only in both create and edit) */}
							<FormField
								control={control}
								name="ticketId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('parkingSessions:form.ticketId')}
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												value={field.value ?? ''}
												readOnly
												className="bg-muted"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Status Field (only in edit mode) */}
							{mode === 'edit' && (
								<FormField
									control={control}
									name="status"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t('parkingSessions:form.status')}
											</FormLabel>
											<Select
												value={field.value}
												onValueChange={field.onChange}
											>
												<FormControl>
													<SelectTrigger className="w-full">
														<SelectValue
															placeholder={t(
																'parkingSessions:form.statusPlaceholder'
															)}
														/>
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{statusOptions.map(option => (
														<SelectItem
															key={option.value}
															value={option.value}
														>
															{t(option.labelKey)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}
						</div>
					</Card>
				</form>
			</Form>
		</>
	);
};
